import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export function PageHeader({
  title,
  description,
  crumbs,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  crumbs?: { label: string; to?: string }[];
  actions?: ReactNode;
}) {
  return (
    <div className="mz-fade-up mb-5 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        {crumbs && crumbs.length > 0 && (
          <nav className="mb-1.5 flex flex-wrap items-center gap-1 text-xs text-[#a77d92]" aria-label="Breadcrumb">
            {crumbs.map((c, i) => (
              <span key={`${c.label}-${i}`} className="flex items-center gap-1">
                {i > 0 && <span className="text-[#dcb6c7]">/</span>}
                {c.to ? (
                  <Link to={c.to} className="font-medium hover:text-[#d6006e]">
                    {c.label}
                  </Link>
                ) : (
                  <span className="font-medium text-[#7a1f4a]">{c.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}
        <h1 className="text-xl font-bold tracking-tight text-[#3b0a22]">{title}</h1>
        {description && <p className="mt-1 max-w-2xl text-sm leading-relaxed text-[#9a6b82]">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ children, className = '', title, subtitle, actions, padded = true }: { children: ReactNode; className?: string; title?: ReactNode; subtitle?: ReactNode; actions?: ReactNode; padded?: boolean }) {
  return (
    <section className={`mz-card ${className}`}>
      {(title || actions) && (
        <div className="flex flex-wrap items-start justify-between gap-2 border-b border-[#fbeef4] px-4 py-3">
          <div>
            {title && <h2 className="text-sm font-bold text-[#3b0a22]">{title}</h2>}
            {subtitle && <p className="mt-0.5 text-xs text-[#a77d92]">{subtitle}</p>}
          </div>
          {actions}
        </div>
      )}
      <div className={padded ? 'p-4' : ''}>{children}</div>
    </section>
  );
}

export function Field({ label, hint, children, htmlFor, className = '' }: { label: string; hint?: ReactNode; children: ReactNode; htmlFor?: string; className?: string }) {
  return (
    <div className={className}>
      <label className="mz-label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {hint && <p className="mz-hint">{hint}</p>}
    </div>
  );
}

export function Toggle({ checked, onChange, disabled, label, size = 'md' }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; label?: string; size?: 'sm' | 'md' }) {
  const dims = size === 'sm' ? { w: 'w-8', h: 'h-[18px]', dot: 'h-3.5 w-3.5', on: 'translate-x-[14px]' } : { w: 'w-10', h: 'h-[22px]', dot: 'h-[18px] w-[18px]', on: 'translate-x-[18px]' };
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
      className={`relative inline-flex ${dims.h} ${dims.w} shrink-0 items-center rounded-full p-0.5 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${checked ? 'bg-[#d6006e]' : 'bg-[#ead3de]'}`}
    >
      <span className={`${dims.dot} rounded-full bg-white shadow transition-transform ${checked ? dims.on : 'translate-x-0'}`} />
    </button>
  );
}

export function Segmented<V extends string>({ value, onChange, options, size = 'md' }: { value: V; onChange: (v: V) => void; options: { value: V; label: string; count?: number }[]; size?: 'sm' | 'md' }) {
  return (
    <div className="inline-flex flex-wrap gap-0.5 rounded-lg bg-[#fbeef4] p-0.5" role="tablist">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(o.value)}
            className={`rounded-md font-semibold transition ${size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-[13px]'} ${
              on ? 'bg-white text-[#d6006e] shadow-sm' : 'text-[#9a6b82] hover:text-[#7a1f4a]'
            }`}
          >
            {o.label}
            {o.count !== undefined && <span className={`ml-1.5 text-[11px] ${on ? 'text-[#d6006e]/70' : 'text-[#b99aaa]'}`}>{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = 'Search…', className = '' }: { value: string; onChange: (v: string) => void; placeholder?: string; className?: string }) {
  return (
    <div className={`relative ${className}`}>
      <svg className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#c9a3b5]" viewBox="0 0 20 20" fill="none" aria-hidden>
        <circle cx="9" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.7" />
        <path d="m13.5 13.5 3 3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mz-input pl-8" type="search" />
    </div>
  );
}

const NOTICE_TONES = {
  success: { bg: '#ecfdf5', bd: '#a7f3d0', fg: '#047857' },
  error: { bg: '#fff1f2', bd: '#fecdd3', fg: '#be123c' },
  warning: { bg: '#fffbeb', bd: '#fde68a', fg: '#b45309' },
  info: { bg: '#fdf2f8', bd: '#f6dbe7', fg: '#9d0050' },
} as const;

export function Notice({ tone = 'info', children, onClose, className = '' }: { tone?: keyof typeof NOTICE_TONES; children: ReactNode; onClose?: () => void; className?: string }) {
  const t = NOTICE_TONES[tone];
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={`mz-pop flex items-start gap-2 rounded-lg px-3 py-2 text-[13px] ${className}`} style={{ background: t.bg, border: `1px solid ${t.bd}`, color: t.fg }}>
      <div className="min-w-0 flex-1">{children}</div>
      {onClose && (
        <button type="button" onClick={onClose} className="shrink-0 opacity-60 hover:opacity-100" aria-label="Dismiss">
          ×
        </button>
      )}
    </div>
  );
}

export function EmptyState({ title, description, action }: { title: string; description?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#fdf2f8] text-[#d6006e]">
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M4 7h16M4 12h16M4 17h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </div>
      <p className="text-sm font-semibold text-[#3b0a22]">{title}</p>
      {description && <p className="mt-1 max-w-sm text-[13px] text-[#a77d92]">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function LoadingRows({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="h-9 animate-pulse rounded-lg bg-[#fbeef4]" style={{ opacity: 1 - i * 0.15 }} />
      ))}
    </div>
  );
}

export function StatCard({ label, value, hint, accent = false }: { label: string; value: ReactNode; hint?: ReactNode; accent?: boolean }) {
  return (
    <div className={`mz-card px-4 py-3.5 ${accent ? '!border-[#f3b9d2]' : ''}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#a77d92]">{label}</p>
      <p className={`mt-1 text-xl font-bold tracking-tight ${accent ? 'text-[#d6006e]' : 'text-[#3b0a22]'}`}>{value}</p>
      {hint && <p className="mt-0.5 text-xs text-[#a77d92]">{hint}</p>}
    </div>
  );
}

export function FormActions({ children }: { children: ReactNode }) {
  return <div className="sticky bottom-0 -mx-4 mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-[#fbeef4] bg-white/95 px-4 py-3 backdrop-blur">{children}</div>;
}
